import React from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Form } from 'formik-antd';
import { Formik } from 'formik';
import { Button, Divider, Alert, notification } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';

import { getPriceByAmount } from 'utils/tokens';
import { ROUTE_EMPLOYER_PREFIX } from 'utils/routes';
import Client from 'containers/app/client';
import LoadingFullPage from 'components/loading-full-page';
import { LayoutPage } from 'components/layouts';

import { ReactComponent as BundleImage01 } from 'svg/illustrations/undraw_cabin_hkfr.svg';
import { ReactComponent as BundleImage02 } from 'svg/illustrations/undraw_yoga_248n.svg';
import { ReactComponent as BundleImage03 } from 'svg/illustrations/undraw_snap_the_moment_oyn6.svg';
import PricingCard from './pricing-card';

import './style.css';

const CARD_OPTIONS = {
  style: {
    base: {
      fontSize: '1.1em',
      color: '#424770',
      letterSpacing: '0.025em',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

const initialValues = {
  amount: null,
};
// bundles for now i guess could be 1 - £3 / 10 - £28 / 20 - £55 / 50 - £130

const bundles = [
  {
    title: 'Single',
    amount: 1,
    image: BundleImage01,
    description: '£10.00 + VAT',
  },
  {
    title: 'Bronze',
    amount: 10,
    image: BundleImage02,
    description: '£95.00 + VAT',
  },
  {
    title: 'Silver',
    amount: 25,
    image: BundleImage03,
    description: '£225.00 + VAT',
  },
];

const tokenDescription = (
  <>
    <p>
      MyShifts works for employers by pre-purchasing ‘Connection Tokens’. These
      are priced at <strong>£10:00 each (+ vat).</strong> Tokens enable the
      employer to make a ‘connection’ with a worker, giving full access to their
      employment history and contact details.
    </p>
    <p>
      The role you are looking for will dictate how many tokens you will need to
      use to see their details. For example, if the role is a Carer or Support
      Worker this will cost <strong>One token.</strong> If you are looking for a
      senior carer/team leader this will require <strong>Two tokens</strong> to
      be used. Finally, if you are looking for a more senior role such as Unit
      Leader or Manager then <strong>Three tokens</strong> will be required.
      This is considerably lower than employing a recruitment agency to find
      suitable workers
    </p>
  </>
);

const tokenFootNote = (
  <>
    <p>
      When you post your MyShifts work opportunity our clever system will find
      suitably matched workers form our massive active Bank of workers.
    </p>
    <p>
      You will at this stage see an anonymised brief summary of each worker who
      matches the profile you have set to fill your vacant MyShifts work
      opportunity. Then you can narrow down your selection and only use token
      for the Workers you feel best suit your service needs.
    </p>
    <p>
      Tokens allow employers to see the full worker profile. After viewing their
      full profile you can decide which worker you wish to ‘connect’ with and
      offer the MyShifts work opportunity to. One token allows you to see One
      worker’s full employment profile (including all information to offer work
      to and employ them as ‘your’ bank worker’
    </p>
  </>
);

const BuyTokens = () => {
  const stripe = useStripe();
  const elements = useElements();
  const history = useHistory();

  const handleSubmit = async (values, { setStatus, setSubmitting }) => {
    setSubmitting(true);
    const cardElement = elements.getElement(CardElement);

    try {
      const { data } = await Client.payToken({
        description: 'Buy Tokens',
        tokens: values.amount,
      });

      if (data == null || data.client_secret == null) {
        throw new Error('Failed to create payment');
      }

      const result = await stripe.confirmCardPayment(data.client_secret, {
        payment_method: {
          card: cardElement,
        },
      });

      const { error, paymentIntent } = result;

      if (error) {
        throw new Error(error);
      } else if (paymentIntent.status === 'succeeded') {
        await Client.addTokens(values.amount);
        notification.success({
          title: 'Transaction complete',
          message: `${values.amount} Tokens added to your account`,
        });
        setSubmitting(false);
        // history.push(`${ROUTE_EMPLOYER_PREFIX}/dashboard`);
        history.push(`/token-purchase`);
      }
    } catch (error) {
      setSubmitting(false);
      console.error(error);
      setStatus({ message: 'Payment failed', type: 'error' });
    }
  };

  if (stripe == null || elements == null) return <LoadingFullPage />;
  return (
    <LayoutPage className="buy-tokens-page">
      <LayoutPage.Container className="buy-tokens-page-container">
        <Formik initialValues={initialValues} onSubmit={handleSubmit}>
          {({ isSubmitting, values, status, setStatus, setFieldValue }) => {
            return (
              <Form layout="vertical">
                {values.amount == null ? (
                  <>
                    <h1>Buy Tokens</h1>
                    <section className="buy-tokens-page-title">
                      <Alert
                        description={tokenDescription}
                        type="info"
                        showIcon
                      />
                      <section className="pricing-options-wrapper">
                        {bundles.map(
                          ({ title, description, amount, image: Image }, i) => (
                            <PricingCard
                              key={i}
                              title={title}
                              amount={amount}
                              description={description}
                              onClick={() => {
                                setFieldValue('amount', amount);
                              }}
                            >
                              <Image className="pricing-card-image" />
                            </PricingCard>
                          )
                        )}
                      </section>
                    </section>
                    <section className="buy-tokens-page-footnote">
                      {tokenFootNote}
                    </section>
                  </>
                ) : null}

                {values.amount != null ? (
                  <>
                    <h1>Buy Tokens</h1>
                    <div className="buy-tokens-checkout">
                      <header className="buy-tokens-checkout-header">
                        <Button
                          type="secondary"
                          className="buy-tokens-back"
                          icon={<ArrowLeftOutlined />}
                          shape="round"
                          onClick={() => {
                            setFieldValue('amount', null);
                          }}
                          disabled={isSubmitting}
                        >
                          Go Back
                        </Button>

                        <h2>
                          Tokens: {values.amount} = £
                          {getPriceByAmount(values.amount)}
                        </h2>
                      </header>
                      {status != null ? (
                        <Alert
                          style={{ marginTop: 'var(--margin-01)' }}
                          closable
                          message={status.message}
                          type={status.type}
                          onClose={() => {
                            setStatus(undefined);
                          }}
                        />
                      ) : null}
                      <Divider />
                      <CardElement style={{}} options={CARD_OPTIONS} />
                      <Divider />
                      <Button
                        style={{ width: '100%' }}
                        type="primary"
                        htmlType="submit"
                        className="login-form-button"
                        loading={isSubmitting}
                        disabled={isSubmitting}
                      >
                        Purchase
                      </Button>
                    </div>
                  </>
                ) : null}
              </Form>
            );
          }}
        </Formik>
      </LayoutPage.Container>
    </LayoutPage>
  );
};

export default BuyTokens;
